import type { Route } from './+types/about';
import { Link } from 'react-router';
import { RelatedPages } from '~/components/related-pages';
import { LeafMark } from '~/components/marketing/leaf-mark';
import { buildMeta, schemaFor } from '~/lib/seo';
import { SITE } from '~/data/site';

// Founder/family photo. Vikelas, his wife and their son on the St. Pete
// waterfront, the Vinoy behind them. Live since 2026-09-10.
// It is 3:4 PORTRAIT (1050x1400) — the <img> below declares those real
// dimensions so the browser reserves the right box and the page does not shift
// when it loads. If this is ever swapped, update width/height to match.
const FOUNDER_PHOTO: string | null = '/images/founder-vikelas.jpg';

export const meta: Route.MetaFunction = () =>
  buildMeta({
    title: 'About',
    description:
      "New Leaf Cleaning was founded in December 2025 by Vikelas Carey. W-2 employees from day one. Built because the alternative wasn't good enough.",
    path: '/about',
    jsonLd: schemaFor('/about'),
  });

export default function AboutPage() {
  return (
    <main id="main-content">
      <section className="section">
        <div className="container-narrow">
          <p className="eyebrow page-eyebrow">Our story</p>
          <h1 className="display-lg page-h1-full">
            Cleaning with care{' '}
            <em className="italic-display">and purpose</em>.
          </h1>
          <p className="lede chapter-lede">
            We&rsquo;re building the most reliable cleaning service in Tampa Bay &mdash; one
            that treats clients, employees, and the community with equal dignity.
          </p>
        </div>
      </section>

      <section className="section-sm">
        <div className="container-narrow">
          {/* Founder photo, live. Kept behind a constant so the branded
              placeholder is still a one-line fallback if the file is ever
              pulled — no risk of shipping a broken <img>. */}
          {FOUNDER_PHOTO ? (
            <figure className="founder-photo reveal">
              <img
                src={FOUNDER_PHOTO}
                alt="Vikelas Carey and his family, founders of New Leaf Cleaning"
                width={1050}
                height={1400}
                loading="lazy"
              />
              <figcaption>Vikelas &middot; Founder &mdash; with my family, a few blocks from where we clean</figcaption>
            </figure>
          ) : (
            <div className="founder-photo founder-photo-empty reveal" aria-hidden="true">
              <LeafMark size={64} fillColor="#B08D57" />
              <span className="founder-photo-name">Vikelas &middot; Founder</span>
            </div>
          )}

          <div className="prose reveal">

            <h2>Meet Vikelas</h2>

            <p>
              Hi, I&rsquo;m Vikelas Carey, founder of New Leaf Cleaning. I started this
              business because I believe companies can be a force for good &mdash; not just
              profit.
            </p>

            <p>
              That&rsquo;s my family in the photo. We live here, a few blocks from most
              of the homes we clean. Having a little one is what made me understand
              this job at all &mdash; we lost entire Saturdays to a house that never quite
              stayed clean, and we were the lucky ones, with two of us and one kid.
            </p>

            <p>
              So I know what I&rsquo;m actually selling, and it isn&rsquo;t a clean
              kitchen &mdash; it&rsquo;s the Saturday you get back. Families are drowning in the
              mental load of keeping a home &mdash; juggling schedules, chasing unreliable
              services, doing it again next week. You don&rsquo;t need cheaper cleaning.
              You need your time back.
            </p>

            <p>
              So in December 2025 I started New Leaf. The brief was simple:
            </p>

            <blockquote className="pullquote">
              Same team every visit. W-2 employees only. Per-square-foot pricing, published
              on the website. No contract. No surprise rate hikes. No subcontracted help.
            </blockquote>

            <p>
              Everything else &mdash; the crew, the training we built from scratch, the pricing
              page that shows every dollar &mdash; comes from following that brief one decision
              at a time.
            </p>

            <p>
              As a Christian, I&rsquo;ve watched too many businesses chase growth while
              forgetting the people who make it possible. I wanted to build something different:
              a company that treats employees with dignity &mdash; W-2, fair wages, real growth
              paths &mdash; and treats clients like partners, not transactions.
            </p>

            <p>
              New Leaf is the first of several businesses I&rsquo;m building for the long run.
              The goal is generational impact that lifts everyone it touches: employees,
              clients, community.
            </p>

            <p className="marginalia chapter-marginalia">
              If this is what you&rsquo;ve been looking for, we&rsquo;d love to take care
              of your home.
            </p>
            <p className="founder-sig"><strong>— Vikelas</strong></p>

          </div>
        </div>
      </section>

      {/* What we stand for */}
      <section className="section-sm bg-bone-warm">
        <div className="container">
          <p className="eyebrow page-eyebrow">What we stand for</p>
          <h2 className="display-md h2-constrained">
            Built different{' '}
            <em className="italic-display">on purpose</em>.
          </h2>
          {/* Compact variant — icons dropped and padding reduced so the three
              values read as a tight row rather than three tall panels. */}
          <div className="leaf-cards leaf-cards-mt leaf-cards-compact">
            <article className="leaf-card reveal">
              <h3>Reliability</h3>
              <p>Same crew, same day, same standard. Our systems make sure nothing falls through the cracks.</p>
            </article>
            <article className="leaf-card reveal">
              <h3>Transparency</h3>
              <p>Per-sqft pricing published on this page. No surprise rate hikes. No contracts. You always know what to expect.</p>
            </article>
            <article className="leaf-card reveal">
              <h3>Dignity</h3>
              <p>Every cleaning technician is a W-2 employee with fair wages and a real growth path. How we treat our team is how they&rsquo;ll treat your home.</p>
            </article>
          </div>
        </div>
      </section>

      <RelatedPages path="/about" />

      <section className="cta-final">
        <div className="container-narrow cta-inner">
          <p className="cta-eyebrow">Ready?</p>
          <h2>See if it&rsquo;s <em>the right fit</em>.</h2>
          <p className="cta-lede">Ninety seconds, your exact price, no pressure.</p>
          <Link to="/quote" className="btn btn-brass btn-lg">See your price →</Link>
        </div>
      </section>
    </main>
  );
}
